import { useEffect, useMemo } from 'react';
import getEnvironment from '@zola-helpers/client/dist/es/util/environment';

declare global {
  interface Window {
    // https://developer.gladly.com/glad-app/Gladly.html
    Gladly: {
      // https://developer.gladly.com/glad-app/Gladly.html#~init()
      init: (config: GladlyConfig) => Promise<void>;
      // https://developer.gladly.com/glad-app/Gladly.html#~setUser()
      setUser: (user: GladlyUser) => void;
      // https://developer.gladly.com/glad-app/Gladly.html#~show()
      show: () => void;
    };
    gladlyConfig: GladlyConfig;
  }
}

// https://developer.gladly.com/glad-app/Gladly.html#.SidekickConfig
interface GladlyConfig {
  appId: string;
  autoShowButton?: boolean;
}

// https://developer.gladly.com/glad-app/Gladly.html#.User
interface GladlyUser {
  name?: string;
  email?: string;
  phoneNumber?: string;
  jwt?: string;
}
export interface GladlyChatProps {
  isChatEnabled?: boolean;
  appId: string;
}

export const useGladlyChat = ({
  isChatEnabled = false,
  appId = 'zola.com-chat',
}: Partial<GladlyChatProps> = {}): void => {
  const gladlyEnvironment = useMemo(
    () => (getEnvironment() === 'production' ? 'PROD' : 'STAGING'),
    []
  );

  useEffect(() => {
    if (isChatEnabled) {
      const isZgSnippetExisting = document.getElementById('zg-snippet') || window.Gladly;
      if (!isZgSnippetExisting) {
        const zgSnippet = document.createElement('script');
        zgSnippet.type = 'text/javascript';
        zgSnippet.async = true;
        zgSnippet.setAttribute('id', 'zg-snippet');
        zgSnippet.innerHTML = `!function(c,n,r,t){if(!c[r]){var i,d,p=[];d="PROD"!==t&&t?"STAGING"===t?"https://cdn.gladly.qa/gladly/chat-sdk/widget.js":t:"https://cdn.gladly.com/chat-sdk/widget.js",c[r]={init:function(){i=arguments;var e={then:function(t){return p.push({type:"t",next:t}),e},catch:function(t){return p.push({type:"c",next:t}),e}};return e}},c.__onHelpAppHostReady__=function(t){if(delete c.__onHelpAppHostReady__,(c[r]=t).loaderCdn=d,i)for(var e=t.init.apply(t,i),n=0;n<p.length;n++){var a=p[n];e="t"===a.type?e.then(a.next):e.catch(a.next)}},function(){try{var t=n.getElementsByTagName("script")[0],e=n.createElement("script");e.async=!0,e.src=d+"?q="+(new Date).getTime(),t.parentNode.insertBefore(e,t)}catch(t){}}()}}
        (window,document,'Gladly','${gladlyEnvironment}')`;
        document.body.appendChild(zgSnippet);

        window.Gladly.init({
          appId,
          autoShowButton: true,
        })
          .then(() => {
            // Custom Button Initialization
            // https://github.com/gladly/sidekick-examples/tree/master/docs/custom-minimized-button
            // This id cannot be changed as the script is looking for this specific id
            const startChatButton = document.getElementById('custom-gladly-chat-button');
            if (!startChatButton) return;

            // An onClick handler can be attached to the "custom-gladly-chat-button" element
            startChatButton.onclick = () => {
              window.Gladly.show();
            };
          })
          .catch((error) => {
            // If anything goes wrong when Glad App is being initialized, this gets called.
            console.error('error:', error);
          });
      }
    }
  }, [appId, gladlyEnvironment, isChatEnabled]);
};

export default useGladlyChat;
